import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { OfficeCity } from '../../core/models/office-city';
import { company, offices, telHref } from '../../site/company';
import { TextLines } from './text-lines';

// Keyed by `OfficeCity` rather than by `string`, which is what makes a city added to the
// vocabulary a compile error here instead of an office with no address under it.
const OFFICE_ADDRESS: Record<OfficeCity, TranslationKey> = {
    Almaty: 'site.contact.office_almaty',
    Astana: 'site.contact.office_astana'
};

const OFFICE_MAP_LABEL: Record<OfficeCity, TranslationKey> = {
    Almaty: 'site.contact.map_almaty',
    Astana: 'site.contact.map_astana'
};

const OFFICE_CITY: Record<OfficeCity, TranslationKey> = {
    Almaty: 'site.city.almaty',
    Astana: 'site.city.astana'
};

/**
 * The heading's type is set on the element on the live page rather than in the stylesheet, which
 * overrides the smaller `.contact-info-title` size the class would give it. Carrying the override
 * across keeps the heading the size it is today; folding it into the class would change every other
 * page that uses one.
 */
const HEADING_STYLE = {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(1.6rem, 3vw, 2.4rem)',
    fontWeight: 700,
    color: 'var(--gray-900)',
    marginBottom: 16,
    lineHeight: 1.25
} as const;

/** How to reach the company: the details on the left, the opening hours and the two maps on the right. */
export function ContactSection() {
    const { t } = useTranslation();
    // Almaty before Astana, which is the order the live page lists them in — `offices` is keyed
    // to the booking flow's order, and the two are not the same list for the same reason.
    const almaty = offices.find((office) => office.city === 'Almaty');
    const astana = offices.find((office) => office.city === 'Astana');
    const listed = [almaty, astana].filter((office) => office !== undefined);

    return (
        <section className="section section-alt" id="contact">
            <div className="container">
                <div className="contact-grid">
                    <div>
                        <span className="section-label">{t('site.contact.label')}</span>
                        <h2 className="contact-info-title" style={HEADING_STYLE}>
                            <TextLines text={t('site.contact.title')} />
                        </h2>
                        <p className="contact-info-desc">{t('site.contact.desc')}</p>

                        <div className="contact-details">
                            <div className="contact-item">
                                <div className="contact-item-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.39 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.54a16 16 0 0 0 6.55 6.55l.92-.92a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="contact-item-label">{t('site.contact.phone')}</div>
                                    <div className="contact-item-value">
                                        <a href={telHref(company.phone)}>{company.phone}</a>
                                    </div>
                                </div>
                            </div>

                            <div className="contact-item">
                                <div className="contact-item-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                        <polyline points="22,6 12,13 2,6" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="contact-item-label">Email</div>
                                    <div className="contact-item-value">
                                        <a href="mailto:ipotekalyk_dariger1@mail.ru">
                                            ipotekalyk_dariger1@mail.ru
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="contact-item">
                                <div className="contact-item-icon">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="contact-item-label">{t('site.contact.office')}</div>
                                    {almaty !== undefined && (
                                        <>
                                            <div className="contact-item-value">
                                                {t(OFFICE_ADDRESS.Almaty)}
                                            </div>
                                            <MapLink href={almaty.mapUrl} label={t('site.contact.open_2gis')} />
                                        </>
                                    )}
                                    <hr />
                                    {astana !== undefined && (
                                        <>
                                            <div className="contact-item-value">
                                                {t(OFFICE_ADDRESS.Astana)}
                                            </div>
                                            <MapLink href={astana.mapUrl} label={t('site.contact.open_2gis')} />
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="contact-item">
                                <div className="contact-item-icon">
                                    <svg viewBox="0 0 24 24">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                </div>
                                <div>
                                    <div className="contact-item-label">{t('site.contact.hours')}</div>
                                    <div className="contact-item-value">{t('site.contact.hours_val')}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="contact-info-panel">
                        <div className="contact-panel-title">{t('site.contact.schedule')}</div>
                        <div className="contact-panel-sub">
                            {t('site.contact.schedule_sub1_line1')}
                            <br />
                            {t('site.contact.schedule_sub1_prefix')}
                            <strong>{t('site.contact.schedule_sub1_hours')}</strong>
                            {t('site.contact.schedule_sub1_suffix')}
                        </div>
                        <div className="contact-panel-sub">
                            {t('site.contact.schedule_sub2_prefix')}
                            <strong>{t('site.contact.schedule_sub2_free')}</strong>
                        </div>

                        <div className="contact-panel-divider" />

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                            {listed.map((office) => (
                                <a
                                    key={office.city}
                                    href={office.mapUrl}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="contact-map-embed"
                                >
                                    <div className="contact-map-visual">
                                        <div className="contact-map-pin">
                                            <svg viewBox="0 0 24 24">
                                                <circle cx="12" cy="10" r="4" fill="white" />
                                            </svg>
                                        </div>
                                        <div className="contact-map-text">
                                            <strong>{t(OFFICE_MAP_LABEL[office.city])}</strong>
                                            <small>{t(OFFICE_CITY[office.city])}</small>
                                        </div>
                                    </div>
                                    <div className="contact-map-footer">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                        <span>{t('site.contact.open_2gis')}</span>
                                        <svg viewBox="0 0 24 24" style={{ marginLeft: 'auto' }}>
                                            <polyline points="9 18 15 12 9 6" />
                                        </svg>
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function MapLink({ href, label }: { href: string; label: string }) {
    return (
        <a href={href} target="_blank" rel="noreferrer noopener" className="contact-map-link">
            <svg viewBox="0 0 24 24">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
            </svg>
            <span>{label}</span>
            <svg viewBox="0 0 24 24" style={{ width: 12, height: 12 }}>
                <polyline points="9 18 15 12 9 6" />
            </svg>
        </a>
    );
}
